import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Box,
    Chip,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import StageTransitionAnalyticsService from '../../services/StageTransitionAnalyticsService';

/** Column definitions for the four forward transitions. */
const TRANSITIONS = [
    { key: 'aiToL1',        label: 'AI → L1',        description: 'From AI-Complete to L1-Complete' },
    { key: 'l1ToL2',        label: 'L1 → L2',        description: 'From L1-Complete to L2-Complete' },
    { key: 'l2ToL3',        label: 'L2 → L3',        description: 'From L2-Complete to L3-Complete' },
    { key: 'l3ToPublished', label: 'L3 → Published', description: 'From L3-Complete to Published' },
];

const DATE_RANGE_OPTIONS = [
    { value: 'last7days',  label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'last90days', label: 'Last 90 Days' },
    { value: 'all',        label: 'All Time' },
];

/**
 * Stage Transition Analytics component.
 *
 * Shows:
 *  - A summary table grouped by language (avg, median, p90 per transition).
 *  - A secondary table grouped by video-duration bucket (for VIDEO or ALL content types).
 *
 * Regression handling is done server-side: if a file is reviewed multiple times at a
 * stage, all review cycles are accumulated into a single total per item.
 */
export default function StageTransitionAnalytics({ folderPath, selectedLanguage = '' }) {
    const [data, setData]           = useState(null);
    const [loading, setLoading]     = useState(true);
    const [dateRange, setDateRange] = useState('last30days');
    const [contentType, setContentType] = useState('ALL');
    const [statMetric, setStatMetric]   = useState('avgMs'); // avgMs | medianMs | p90Ms

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateRange, contentType, selectedLanguage, folderPath]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const result = await StageTransitionAnalyticsService.getAnalytics({
                contentType,
                language:    selectedLanguage || '',
                dateRange,
                folderPath:  folderPath || '',
            });
            setData(result);
        } catch (err) {
            console.error('Error fetching stage transition analytics:', err);
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    const fmt = (statsObj) => {
        if (!statsObj || statsObj.count === 0) return <span style={{ color: '#aaa' }}>—</span>;
        const ms = statsObj[statMetric];
        if (ms == null) return <span style={{ color: '#aaa' }}>—</span>;
        return (
            <Tooltip
                title={
                    <Box sx={{ fontSize: 12 }}>
                        <div>avg: {StageTransitionAnalyticsService.formatDuration(statsObj.avgMs)}</div>
                        <div>median: {StageTransitionAnalyticsService.formatDuration(statsObj.medianMs)}</div>
                        <div>p90: {StageTransitionAnalyticsService.formatDuration(statsObj.p90Ms)}</div>
                        <div>min: {StageTransitionAnalyticsService.formatDuration(statsObj.minMs)}</div>
                        <div>max: {StageTransitionAnalyticsService.formatDuration(statsObj.maxMs)}</div>
                        <div style={{ marginTop: 4, color: '#ccc' }}>n={statsObj.count}</div>
                    </Box>
                }
            >
                <span style={{ cursor: 'help', borderBottom: '1px dashed #888' }}>
                    {StageTransitionAnalyticsService.formatDuration(ms)}
                </span>
            </Tooltip>
        );
    };

    const TransitionTable = ({ rows, rowKeyField, rowLabelHeader }) => (
        <TableContainer component={Paper} variant="outlined">
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>{rowLabelHeader}</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 600, width: 60 }}>Items</TableCell>
                        {TRANSITIONS.map((t) => (
                            <TableCell key={t.key} align="center" sx={{ fontWeight: 600 }}>
                                <Tooltip title={t.description}>
                                    <span>{t.label}</span>
                                </Tooltip>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                                No data for the selected filters.
                            </TableCell>
                        </TableRow>
                    ) : (
                        rows.map((row) => (
                            <TableRow key={row[rowKeyField]} hover>
                                <TableCell>{row[rowKeyField]}</TableCell>
                                <TableCell align="center">
                                    <Chip label={row.count} size="small" variant="outlined" />
                                </TableCell>
                                {TRANSITIONS.map((t) => (
                                    <TableCell key={t.key} align="center">
                                        {fmt(row[t.key])}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <Box sx={{ width: '100%', mt: 2 }}>
            {/* Header + filters */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Stage Progression Pace</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Time to progress through each review stage, per language.
                        Hover a cell for full stats. Regressions are accumulated.
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {/* Content type toggle */}
                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={contentType}
                        onChange={(_, v) => v && setContentType(v)}
                    >
                        <ToggleButton value="ALL">All</ToggleButton>
                        <ToggleButton value="VIDEO">Videos</ToggleButton>
                        <ToggleButton value="TEXT_FILE">Text Files</ToggleButton>
                    </ToggleButtonGroup>

                    {/* Date range */}
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                        <InputLabel>Date Range</InputLabel>
                        <Select
                            value={dateRange}
                            label="Date Range"
                            onChange={(e) => setDateRange(e.target.value)}
                        >
                            {DATE_RANGE_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Metric selector */}
                    <FormControl size="small" sx={{ minWidth: 110 }}>
                        <InputLabel>Show</InputLabel>
                        <Select
                            value={statMetric}
                            label="Show"
                            onChange={(e) => setStatMetric(e.target.value)}
                        >
                            <MenuItem value="avgMs">Average</MenuItem>
                            <MenuItem value="medianMs">Median</MenuItem>
                            <MenuItem value="p90Ms">P90</MenuItem>
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Active filter chips */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                <Chip size="small" variant="outlined" label={`Range: ${DATE_RANGE_OPTIONS.find(o => o.value === dateRange)?.label}`} />
                <Chip size="small" variant="outlined" label={`Type: ${contentType}`} />
                {selectedLanguage && <Chip size="small" variant="outlined" label={`Language: ${selectedLanguage}`} />}
                {folderPath && folderPath !== '/' && <Chip size="small" variant="outlined" label={`Folder: ${folderPath}`} />}
                <Tooltip title="Times shown are cumulative: regressions (e.g. L2→L1→L2) add both review cycles together.">
                    <Chip
                        size="small"
                        variant="outlined"
                        icon={<InfoOutlinedIcon />}
                        label="Regressions accumulated"
                        sx={{ cursor: 'help' }}
                    />
                </Tooltip>
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    {/* By-language table */}
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>By Language</Typography>
                    <TransitionTable
                        rows={data?.byLanguage ?? []}
                        rowKeyField="language"
                        rowLabelHeader="Language"
                    />

                    {/* By-duration bucket table (videos only) */}
                    {(contentType === 'VIDEO' || contentType === 'ALL') &&
                     data?.byVideoDuration?.length > 0 && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                                By Video Duration (Videos Only)
                            </Typography>
                            <TransitionTable
                                rows={data.byVideoDuration}
                                rowKeyField="bucket"
                                rowLabelHeader="Duration"
                            />
                        </Box>
                    )}

                    {/* Total items footnote */}
                    {data?.totalItems != null && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                            {data.totalItems} content item{data.totalItems !== 1 ? 's' : ''} with at least one stage transition in the selected range.
                        </Typography>
                    )}
                </>
            )}
        </Box>
    );
}
